import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { User } from '../../services/authService';
import { Movie } from '../../types/Movie';
import { movieService } from '../../services/movieService';
import PageHeader from '../common/PageHeader';
import './MovieDetail.css';

interface MovieDetailProps {
  user: User | null;
  onToggleSidebar: () => void;
}

const MovieDetail: React.FC<MovieDetailProps> = ({ user, onToggleSidebar }) => {
  const navigate = useNavigate();
  const { movieId } = useParams<{ movieId: string }>();
  const [movie, setMovie] = useState<Movie | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreatingNote, setIsCreatingNote] = useState(false);

  // Load movie data
  const loadMovie = useCallback(async () => {
    if (!movieId) return;

    setLoading(true);
    setError(null);

    try {
      const movieData = await movieService.getMovieById(movieId);
      if (movieData) {
        setMovie(movieData);
      } else {
        setError('Movie not found');
      }
    } catch (err: any) {
      console.error('Error loading movie:', err);
      setError('Failed to load movie');
    } finally {
      setLoading(false);
    }
  }, [movieId]);

  useEffect(() => {
    if (movieId) {
      loadMovie();
    }
  }, [movieId, loadMovie]);

  // Handle view notes
  const handleViewNotes = async () => {
    if (!user || !movie || isCreatingNote) return;

    setIsCreatingNote(true);
    try {
      // Check if note already exists
      let noteId = await movieService.getMovieNoteId(movie.id);

      // If note doesn't exist, create it
      if (!noteId) {
        noteId = await movieService.createMovieNote(movie.id, user.id, movie.name);
      }

      // Navigate to note
      if (noteId) {
        navigate(`/notes/${noteId}`);
      }
    } catch (error) {
      console.error('Error accessing movie notes:', error);
      setError('Failed to access notes');
    } finally {
      setIsCreatingNote(false);
    }
  };

  if (!user) {
    return (
      <div className="movie-detail-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Movie Details</h1>}
        />
        <div className="movie-detail-auth-message">
          Please sign in to access movie details.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="movie-detail-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Loading...</h1>}
        />
        <div className="movie-detail-loading">
          <div className="loading-spinner"></div>
          <p>Loading movie details...</p>
        </div>
      </div>
    );
  }

  if (error && !movie) {
    return (
      <div className="movie-detail-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Error</h1>}
        />
        <div className="movie-detail-error">
          <p>{error}</p>
          <button onClick={loadMovie} className="movie-detail-retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  if (!movie) {
    return (
      <div className="movie-detail-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Movie Not Found</h1>}
        />
        <div className="movie-detail-error">
          <p>The movie you're looking for doesn't exist.</p>
          <button onClick={() => navigate('/movies')} className="movie-detail-retry-btn">Back to Movies</button>
        </div>
      </div>
    );
  }

  return (
    <div className="movie-detail-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-center-title">{movie.name}</h1>}
      />

      <div className="movie-detail-content">
        {/* Movie Info Section */}
        <div className="movie-detail-info-section">
          <div className="movie-detail-poster-container">
            {movie.posterUrl ? (
              <img 
                src={movie.posterUrl} 
                alt={`${movie.name} poster`}
                className="movie-detail-poster"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            ) : (
              <div className="movie-detail-poster-placeholder">
                🎬
              </div>
            )}
          </div>
          <div className="movie-detail-info">
            <h2 className="movie-detail-name">{movie.name}</h2>
            {movie.director && (
              <p className="movie-detail-director">by {movie.director}</p>
            )}
            {movie.year && (
              <p className="movie-detail-year">Released: {movie.year}</p>
            )}
            {movie.genre && (
              <p className="movie-detail-genre">{movie.genre}</p>
            )}
            {movie.rating && (
              <p className="movie-detail-rating">⭐ {movie.rating}/5</p>
            )}
            {movie.duration && (
              <p className="movie-detail-duration">{movie.duration} minutes</p>
            )}
            {movie.status && (
              <p className="movie-detail-status">
                Status: {
                  movie.status === 'want_to_watch' ? 'Want to Watch' :
                  movie.status === 'watching' ? 'Watching' :
                  movie.status === 'completed' ? 'Completed' :
                  movie.status === 'paused' ? 'Paused' :
                  movie.status === 'abandoned' ? 'Abandoned' :
                  movie.status
                }
              </p>
            )}
          </div>
        </div>

        {/* Movie Notes Section */}
        <div className="movie-detail-notes-section">
          <h3 className="movie-detail-section-title">Movie Notes</h3>
          <p className="movie-detail-notes-description">
            Capture your thoughts, reviews, and reflections about this movie.
          </p>
          <button
            type="button"
            onClick={handleViewNotes}
            className="movie-detail-notes-btn"
            disabled={isCreatingNote}
          >
            {isCreatingNote ? 'Creating...' : '📝 View Movie Notes'}
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="movie-detail-error-message">
            {error}
            <button onClick={() => setError(null)} className="movie-detail-error-dismiss">✕</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MovieDetail;

